import { shallowRef } from "vue"

export type BusinessContextRequestPolicy = "rebind" | "cancel-stale"

export type BusinessContextRequestBarrierResolution = {
  requestPolicy: BusinessContextRequestPolicy
}

const activeRequestBarrier = shallowRef<Promise<unknown>>()

function resolveRequestPolicy(value: unknown): BusinessContextRequestPolicy {
  if (
    value
    && typeof value === "object"
    && "requestPolicy" in value
    && value.requestPolicy === "rebind"
  ) {
    return "rebind"
  }
  return "cancel-stale"
}

function useBusinessContextRequestBarrier() {
  const registerRequestBarrier = <T>(request: Promise<T>) => {
    if (activeRequestBarrier.value) {
      return activeRequestBarrier.value as Promise<T>
    }
    activeRequestBarrier.value = request
    request.then(
      () => removeRequestBarrier(request),
      () => removeRequestBarrier(request),
    )
    return request
  }

  const removeRequestBarrier = (request: Promise<unknown>) => {
    if (activeRequestBarrier.value === request) {
      activeRequestBarrier.value = undefined
    }
  }

  const clearRequestBarrierForTest = () => {
    activeRequestBarrier.value = undefined
  }

  const waitForBusinessContextReady = async (): Promise<BusinessContextRequestBarrierResolution> => {
    let requestPolicy: BusinessContextRequestPolicy = "rebind"
    // A finishing migration may synchronously hand ownership to the next queued
    // migration. Re-read after every completion so ordinary requests cannot pass
    // through the hand-off gap.
    while (activeRequestBarrier.value) {
      const pendingRequest = activeRequestBarrier.value
      const resolution = await pendingRequest
      if (resolveRequestPolicy(resolution) === "cancel-stale") {
        requestPolicy = "cancel-stale"
      }
    }
    return { requestPolicy }
  }

  return {
    activeRequestBarrier,
    registerRequestBarrier,
    removeRequestBarrier,
    clearRequestBarrierForTest,
    waitForBusinessContextReady,
  }
}

export { useBusinessContextRequestBarrier }
